/**
 * Central client-side error reporter (US-039, S-P4-2). Both app-shell error
 * boundaries (`app/global-error.tsx`, `app/[locale]/(store)/error.tsx`) call
 * THIS one function instead of each re-implementing its own `fetch`/tagging —
 * "error platform tag set centrally, not per-component" applies to client
 * errors too, not just server ones (`instrumentation.ts`'s `onRequestError`).
 *
 * Fire-and-forget: never throws, never blocks the UI. The report is relayed to
 * `POST /api/observability/client-error`, a Route Handler that runs
 * server-side so it can record the exception on the active OTel span and log
 * it through `lib/logger.ts` (trace-correlated, PII-scrubbed, tenant-tagged).
 *
 * Only `message`/`name`/`digest`/`boundary`/`path` are sent — never component
 * state, cart contents, or any other diner data a boundary might have in
 * scope. `path` is `location.pathname` only (no query string, which could
 * carry an address/promo code) — `/api/*` routes are excluded from proxy.ts's
 * matcher, so this Route Handler has no `x-pathname` of its own to fall back on.
 */
export function reportClientError(
  error: Error & { digest?: string },
  boundary: "global" | "store"
): void {
  try {
    const body = JSON.stringify({
      message: error.message,
      name: error.name,
      digest: error.digest,
      boundary,
      path: typeof location !== "undefined" ? location.pathname : undefined,
    })

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" })
      navigator.sendBeacon("/api/observability/client-error", blob)
      return
    }

    void fetch("/api/observability/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Reporting must never itself break the error boundary it's called from.
  }
}
